// Moderation queue — R-63: any feed item can be reported to Group Admin/Org Admin.

import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { listReports, resolveReport, type ReportedItem } from '../lib/api';
import { timeAgo } from '../lib/timeAgo';

export function ModerationPage() {
  const { user } = useAuth();
  const [reports, setReports] = useState<ReportedItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<'open' | 'resolved'>('open');

  useEffect(() => {
    if (!user) return;
    listReports(user.orgId)
      .then((r) => { setReports(r); setLoading(false); })
      .catch(() => setLoading(false));
  }, [user]);

  if (!user) return null;

  const visible = reports.filter((r) => r.status === tab);

  async function handleResolve(reportId: string) {
    await resolveReport(user!.orgId, reportId);
    setReports((prev) =>
      prev.map((r) => r.id === reportId ? { ...r, status: 'resolved' as const } : r),
    );
  }

  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="page-title">Moderation</h1>
          <p className="page-subtitle">Reported feed content for {user.orgName}.</p>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 'var(--space-2)', marginBottom: 'var(--space-5)' }}>
        {(['open', 'resolved'] as const).map((t) => (
          <button
            key={t}
            className={`btn btn--sm ${tab === t ? 'btn--primary' : 'btn--secondary'}`}
            onClick={() => setTab(t)}
          >
            {t === 'open' ? 'Open' : 'Resolved'}
            {' '}
            <span className="badge badge--neutral" style={{ marginLeft: '4px' }}>
              {reports.filter((r) => r.status === t).length}
            </span>
          </button>
        ))}
      </div>

      {loading && <p style={{ color: 'var(--color-text-muted)' }}>Loading…</p>}

      {!loading && visible.length === 0 && (
        <div className="empty-state">
          <p className="empty-state__title">
            {tab === 'open' ? 'No open reports' : 'No resolved reports'}
          </p>
          <p className="empty-state__body">
            {tab === 'open'
              ? 'Reported feed items will appear here for review.'
              : 'Resolved reports are kept for your audit record.'}
          </p>
        </div>
      )}

      {!loading && visible.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
          {visible.map((report) => (
            <div key={report.id} className="card">
              <div style={{ display: 'flex', gap: 'var(--space-4)', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <blockquote
                    style={{
                      borderLeft: '3px solid var(--color-border)',
                      paddingLeft: 'var(--space-3)',
                      fontStyle: 'italic',
                      color: 'var(--color-text-muted)',
                      fontSize: '0.9375rem',
                      marginBottom: 'var(--space-3)',
                    }}
                  >
                    "{report.excerpt}"
                  </blockquote>
                  <div
                    style={{
                      display: 'flex',
                      gap: 'var(--space-4)',
                      fontSize: '0.8125rem',
                      color: 'var(--color-text-subtle)',
                      flexWrap: 'wrap',
                    }}
                  >
                    <span>Reported by {report.reporterName}</span>
                    <span>{timeAgo(report.createdAt)}</span>
                    <span>Reason: {report.reason}</span>
                  </div>
                </div>
                {tab === 'open' && (
                  <div style={{ display: 'flex', gap: 'var(--space-2)', flexShrink: 0 }}>
                    <button
                      className="btn btn--secondary btn--sm"
                      onClick={() => handleResolve(report.id)}
                    >
                      Dismiss
                    </button>
                    <button className="btn btn--danger btn--sm">
                      Remove post
                    </button>
                  </div>
                )}
                {tab === 'resolved' && (
                  <span className="badge badge--success">Resolved</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}
